/**
 * Client Feedback Report
 * Summary reports by client with project-level drill-down
 * MD3 Design
 */
import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api, toast } from "../App";
import {
  BarChart3,
  Building2,
  FolderOpen,
  AlertCircle,
  MessageSquare,
  RefreshCw,
  FileText,
  ChevronDown,
  ChevronRight,
  TrendingUp,
} from "lucide-react";

export default function ClientFeedbackReport({ user }) {
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState("all");
  const [expandedClients, setExpandedClients] = useState({});
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    fetchClients();
    fetchSummary();
    fetchTimeline();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await api.get("/clients");
      setClients(res.data);
    } catch (error) {
      console.error("Failed to load clients");
    }
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedClient !== "all") params.append("client_id", selectedClient);
      if (dateRange.start_date) params.append("start_date", dateRange.start_date);
      if (dateRange.end_date) params.append("end_date", dateRange.end_date);

      const res = await api.get(`/client-feedback/report/summary?${params}`);
      setSummary(res.data);
    } catch (error) {
      toast.error("Failed to load summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeline = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedClient !== "all") params.append("client_id", selectedClient);
      params.append("months", "12");

      const res = await api.get(`/client-feedback/report/timeline?${params}`);
      setTimeline(res.data);
    } catch (error) {
      console.error("Failed to load timeline");
    }
  };

  const handleFilter = () => {
    fetchSummary();
    fetchTimeline();
  };

  const toggleClientExpand = (clientId) => {
    setExpandedClients((prev) => ({
      ...prev,
      [clientId]: !prev[clientId],
    }));
  };

  const getTypeColor = (type) => {
    const colors = {
      Error: "bg-red-500",
      Clarification: "bg-purple-500",
      Change: "bg-orange-500",
      "Additional Information": "bg-cyan-500",
      Void: "bg-gray-500",
      "Pending Review": "bg-amber-500",
    };
    return colors[type] || "bg-gray-400";
  };

  const calculateTypePercentage = (byType, total) => {
    if (total === 0) return {};
    const result = {};
    Object.keys(byType).forEach((type) => {
      result[type] = Math.round((byType[type] / total) * 100);
    });
    return result;
  };

  return (
    <Layout user={user}>
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-md3-on-surface">Feedback Reports</h1>
          <p className="text-sm text-md3-on-surface-variant">
            Client feedback summary and analytics
          </p>
        </div>
        <Button onClick={handleFilter} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-md3-surface border-md3-outline-variant">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={dateRange.start_date}
                onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                className="w-[150px]"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={dateRange.end_date}
                onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                className="w-[150px]"
              />
            </div>
            <Button onClick={handleFilter}>Apply Filter</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-md3-primary-container border-none">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-md3-on-primary-container/70">Total Feedback</p>
                  <p className="text-3xl font-bold text-md3-on-primary-container">
                    {summary.total_feedback}
                  </p>
                </div>
                <MessageSquare className="h-10 w-10 text-md3-on-primary-container/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-md3-secondary-container border-none">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-md3-on-secondary-container/70">Total Clients</p>
                  <p className="text-3xl font-bold text-md3-on-secondary-container">
                    {summary.total_clients}
                  </p>
                </div>
                <Building2 className="h-10 w-10 text-md3-on-secondary-container/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-md3-tertiary-container border-none">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-md3-on-tertiary-container/70">Avg per Client</p>
                  <p className="text-3xl font-bold text-md3-on-tertiary-container">
                    {summary.total_clients > 0
                      ? (summary.total_feedback / summary.total_clients).toFixed(1)
                      : 0}
                  </p>
                </div>
                <TrendingUp className="h-10 w-10 text-md3-on-tertiary-container/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Timeline Chart (Simple Bar) */}
      {timeline.length > 0 && (
        <Card className="bg-md3-surface border-md3-outline-variant">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Monthly Trend (Last 12 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-40">
              {timeline
                .slice()
                .reverse()
                .map((month) => {
                  const maxTotal = Math.max(...timeline.map((t) => t.total), 1);
                  const height = (month.total / maxTotal) * 100;
                  return (
                    <div key={month.month} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-md3-primary rounded-t-sm transition-all hover:bg-md3-primary/80"
                        style={{ height: `${height}%`, minHeight: month.total > 0 ? "8px" : "2px" }}
                        title={`${month.month}: ${month.total} feedback`}
                      />
                      <span className="text-xs text-md3-on-surface-variant mt-1 rotate-45 origin-left">
                        {month.month.slice(5)}
                      </span>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client Summary with Drill-down */}
      <Card className="bg-md3-surface border-md3-outline-variant">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Client Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-md3-on-surface-variant">Loading...</p>
          ) : !summary || summary.clients.length === 0 ? (
            <p className="text-center py-8 text-md3-on-surface-variant">No feedback data found</p>
          ) : (
            <div className="space-y-3">
              {summary.clients.map((client) => {
                const isExpanded = expandedClients[client.client_id];
                const typePercentages = calculateTypePercentage(client.by_type, client.total_feedback);

                return (
                  <div
                    key={client.client_id}
                    className="border border-md3-outline-variant rounded-lg overflow-hidden"
                  >
                    {/* Client Header */}
                    <div
                      className="p-4 bg-md3-surface-container cursor-pointer hover:bg-md3-surface-container-high transition-colors"
                      onClick={() => toggleClientExpand(client.client_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-md3-on-surface-variant" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-md3-on-surface-variant" />
                          )}
                          <Building2 className="h-5 w-5 text-md3-primary" />
                          <div>
                            <p className="font-medium">{client.client_name}</p>
                            <p className="text-sm text-md3-on-surface-variant">
                              {client.projects.length} project(s)
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-md3-primary">
                              {client.total_feedback}
                            </p>
                            <p className="text-xs text-md3-on-surface-variant">Total Feedback</p>
                          </div>
                        </div>
                      </div>

                      {/* Type Distribution Bar */}
                      <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-md3-surface-container-highest">
                        {Object.entries(client.by_type).map(([type, count]) =>
                          count > 0 ? (
                            <div
                              key={type}
                              className={`${getTypeColor(type)} transition-all`}
                              style={{ width: `${typePercentages[type]}%` }}
                              title={`${type}: ${count}`}
                            />
                          ) : null
                        )}
                      </div>

                      {/* Type Legend */}
                      <div className="mt-2 flex flex-wrap gap-3">
                        {Object.entries(client.by_type).map(([type, count]) =>
                          count > 0 ? (
                            <div key={type} className="flex items-center gap-1 text-xs">
                              <div className={`w-2 h-2 rounded-full ${getTypeColor(type)}`} />
                              <span className="text-md3-on-surface-variant">
                                {type}: {count}
                              </span>
                            </div>
                          ) : null
                        )}
                      </div>
                    </div>

                    {/* Project Drill-down */}
                    {isExpanded && client.projects.length > 0 && (
                      <div className="border-t border-md3-outline-variant">
                        {client.projects.map((project, idx) => (
                          <div
                            key={project.project_id}
                            className={`p-4 pl-12 flex items-center justify-between ${
                              idx > 0 ? "border-t border-md3-outline-variant/50" : ""
                            } hover:bg-md3-surface-container-low`}
                          >
                            <div className="flex items-center gap-3">
                              <FolderOpen className="h-4 w-4 text-md3-on-surface-variant" />
                              <div>
                                <p className="font-medium text-sm">
                                  {project.project_number} - {project.project_name}
                                </p>
                                <div className="flex gap-2 mt-1">
                                  {Object.entries(project.by_type).map(([type, count]) =>
                                    count > 0 ? (
                                      <Badge
                                        key={type}
                                        variant="outline"
                                        className="text-xs py-0"
                                      >
                                        {type}: {count}
                                      </Badge>
                                    ) : null
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-semibold">{project.total_feedback}</p>
                              <div className="flex gap-1">
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-amber-50 text-amber-700"
                                >
                                  P: {project.by_status.Pending}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-blue-50 text-blue-700"
                                >
                                  R: {project.by_status["Under Review"]}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-green-50 text-green-700"
                                >
                                  D: {project.by_status.Reviewed}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
}
